import React, { useState, useEffect } from "react";
import { useRADDispatch } from "../RADHandler/contexts/RADDispatchContext.jsx";
import { useRADSelection } from "../RADHandler/contexts/RADSelectionContext.jsx";
import { CSButton } from "@cadenza/ui";
import ComponentForm from "./components/ComponentForm.jsx";
import InteractionEditor from "./components/InteractionEditor.jsx";
import { useMessages } from "@cadenza/messages";
import { deepCopy } from "@cadenza/rad-domain";

const RADElementEditor = () => {
  const {
    updateRole,
    updateActivity,
    updateThread,
    updateStateline,
    deleteElement,
    addSubthread,
    detachSubthread,
    attachSubthread,
    moveSubthread,
    toggleShowLoop,
    deleteInteraction,
    deleteInteractionSegment,
    unmergeThread,
    updatePosition,
  } = useRADDispatch();
  const {
    selectedComponent,
    setSelectedComponent,
    setIsConnecting,
    setIsLooping,
    setIsMerging,
  } = useRADSelection();
  const [editComponent, setEditComponent] = useState(
    selectedComponent ? deepCopy(selectedComponent) : null
  );
  const [active, setActive] = useState(false);
  const { success, error } = useMessages();

  useEffect(() => {
    setEditComponent(selectedComponent ? deepCopy(selectedComponent) : null);
    setActive(false);
  }, [selectedComponent]);

  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;
    setEditComponent((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setActive(true);
  };

  const handleThreadAction = ({ action, editComponent }) => {
    if (!editComponent) return;
    switch (action) {
      case "merge": {
        setIsMerging([editComponent]);
        success(`Merge mode started. Click target or press ESC to cancel.`);
        break;
      }
      case "unmerge": {
        const result = unmergeThread({ thread_id: editComponent.id });
        if (result?.success) {
          success(result.message);
        } else {
          error(result?.message || "Failed to unmerge thread");
        }
        setSelectedComponent(null);
        break;
      }
      default:
        error(`Invalid thread action: ${action}`);
        return;
    }
  };

  const handleSubthreadAction = ({ action, editComponent }) => {
    if (!editComponent) return;
    switch (action) {
      case "insertAtStart":
      case "addAtEnd":
      case "insertAfter": {
        const parent_id = ["case", "part"].includes(editComponent.type)
          ? editComponent.id
          : editComponent.parent_id;

        const result = addSubthread({
          parent_id,
          action,
          child_id: editComponent.id || undefined,
        });

        if (result?.success) {
          success(result.message);
        } else {
          error(result?.message || "Failed to add subthread");
        }
        break;
      }
      case "detach": {
        // Detach subthread
        const result = detachSubthread({ subthread_id: editComponent.id });
        if (result?.success) {
          success(result.message);
        } else {
          error(result?.message || "Failed to detach subthread");
        }
        setSelectedComponent(null);
        break;
      }
      case "attach":
      case "unloop": {
        // Attach subthread
        const result = attachSubthread({ subthread_id: editComponent.id });
        if (result?.success) {
          success(result.message);
        } else {
          error(result?.message || "Failed to attach subthread");
        }
        setSelectedComponent(null);
        break;
      }
      case "loop": {
        // Loop subthread
        setIsLooping([editComponent]);
        success(
          `Loop mode started. Click target state or press ESC to cancel.`
        );
        break;
      }
      case "showLoop":
      case "hideLoop": {
        // Toggle showLoop property
        const result = toggleShowLoop({ id: editComponent.id });
        if (result?.success) {
          success(result.message);
        } else {
          error(result?.message || "Failed to toggle showLoop");
        }
        break;
      }
      case "shiftLeft": {
        // Shift subthread left
        const result = moveSubthread({
          subthread_id: editComponent.id,
          direction: "left",
        });
        if (result?.success) {
          success(result.message);
        } else {
          error(result?.message || "Failed to shift subthread left");
        }
        break;
      }
      case "shiftRight": {
        // Shift subthread right
        const result = moveSubthread({
          subthread_id: editComponent.id,
          direction: "right",
        });
        if (result?.success) {
          success(result.message);
        } else {
          error(result?.message || "Failed to shift subthread right");
        }
        break;
      }
      default:
        error(`Invalid subthread action: ${action}`);
        return;
    }
  };
  const handleInteraction = ({ action, editComponent }) => {
    if (!editComponent) return;
    switch (action) {
      case "startConnecting": {
        setIsConnecting({ source: editComponent });
        success(
          `Connection mode started. Click target or press ESC to cancel.`
        );
        break;
      }
      case "removeInteraction": {
        const result = deleteInteraction({ component: editComponent });
        if (!result.success) {
          error(result?.message || "Failed to delete interaction");
        } else {
          success(result.message);
        }
        setSelectedComponent(null);
        break;
      }
      case "removeInteractionSegment": {
        // Deprecated
        console.log("Removing interaction segment :", editComponent);
        const { from, to } = editComponent.segment;
        const result = deleteInteractionSegment({
          id: editComponent.id,
          from,
          to,
        });
        if (!result.success) {
          console.error(
            "Failed to delete interaction section:",
            result.message
          );
          error(result?.message || "Failed to delete interaction section");
        } else {
          success(result.message);
        }
        setSelectedComponent(null);
        break;
      }
      default:
        error(`Invalid action: ${action}`);
        return;
    }
  };

  const handleReset = () => {
    setEditComponent(selectedComponent ? deepCopy(selectedComponent) : null);
    setActive(false);
  };

  const handleLabelReset = (type) => {
    if (!editComponent) return;
    const result = updatePosition({
      id: editComponent.id,
      type,
    });
    if (result?.success) {
      success(result.message);
    } else {
      error(result?.message || "Failed to restore label position");
    }
    setActive(false);
  };

  const handleSubmit = () => {
    if (editComponent?.id) {
      if (editComponent?.class === "role") {
        updateRole(editComponent);
      } else if (["activity", "refinement"].includes(editComponent?.class)) {
        updateActivity(editComponent);
      } else if (
        ["thread", "case", "part", "repeat"].includes(editComponent?.class)
      ) {
        updateThread(editComponent);
      } else if (editComponent?.class === "state") {
        updateStateline(editComponent);
      }
    } else {
      console.warn("Invalid component data.");
    }
    setActive(false);
  };

  const handleDelete = () => {
    if (editComponent?.id) {
      console.log("Deleting component:", editComponent);
      const result = deleteElement(editComponent);
      if (result?.success) {
        success(result.message);
      } else {
        error(result?.message || "Failed to delete component");
      }
      setActive(false);
      setEditComponent(null);
      setSelectedComponent(null);
    }
  };

  const handleKeyDown = (event) => {
    if (
      (event.key === "Enter" || event.key === "Tab") &&
      event.target.value.trim()
    ) {
      event.preventDefault();
      const form = event.target.form;
      const index = [...form].indexOf(event.target);
      form.elements[index + 1]?.focus();
    }
  };

  return (
    selectedComponent && (
      <div className="rad-data-container">
        {editComponent &&
          editComponent.class !==
            "connection" /* Dont show the connection editor here */ && (
            <>
              <h3>{`${(editComponent?.type || editComponent?.class || "unknown").charAt(0).toUpperCase() + (editComponent.type || editComponent.class).slice(1)} Editor`}</h3>
              <h4>{`ID: ${editComponent.id || "new component"}`}</h4>
              <form onSubmit={(e) => e.preventDefault()}>
                <fieldset>
                  <br />
                  <ComponentForm
                    editComponent={editComponent}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onSubthreadAction={handleSubthreadAction}
                    onThreadAction={handleThreadAction}
                    onLabelReset={handleLabelReset}
                  />
                </fieldset>
                <div className="button-container">
                  <CSButton disabled={!active} onClick={handleSubmit}>
                    Save
                  </CSButton>
                  <CSButton disabled={!active} onClick={handleReset}>
                    Reset
                  </CSButton>
                  {/* Dont show this button if editComponent.class is 'state' */}
                  {editComponent.class !== "state" && (
                    <CSButton onClick={handleDelete}>Delete</CSButton>
                  )}
                </div>
              </form>
            </>
          )}
        {editComponent &&
          (["role", "connection", "state", "case", "part", "repeat"].includes(
            editComponent.class
          ) ||
            editComponent.type === "interaction") && (
            <form onSubmit={(e) => e.preventDefault()}>
              <fieldset>
                <br />
                <InteractionEditor
                  editComponent={editComponent}
                  onAction={handleInteraction}
                />
              </fieldset>
            </form>
          )}
      </div>
    )
  );
};

export default RADElementEditor;
